import axios from 'axios';
import { t } from 'i18next';

/**
 * Машиночитаемые коды отказов `client/otp-auth`. Ветвиться нужно по ним:
 * текст сообщения приходит на языке из заголовка `Language` и меняется без
 * предупреждения, а код — часть контракта.
 */
export type OtpAuthErrorCode =
  | 'invalid_phone_format'
  | 'invalid_name'
  | 'invalid_data'
  | 'invalid_code'
  | 'code_expired'
  | 'resend_too_soon'
  | 'too_many_requests'
  /** Пять неверных кодов подряд: `verify-otp` заперт до `retry_after`. */
  | 'too_many_attempts'
  | 'sms_failed'
  | 'server_error'
  /** Сеть, таймаут, 500 без тела — всё, где кода не нашлось. */
  | 'unknown';

export type OtpAuthError = {
  code: OtpAuthErrorCode;
  message: string;
  title?: string;
  /** Секунды до следующей попытки — у `resend_too_soon` и `too_many_attempts`. */
  retryAfter?: number;
  status?: number;
};

type ErrorBody = {
  error?: {
    code?: string;
    message?: string;
    title?: string;
    retry_after?: number;
  };
  message?: string;
};

/**
 * Приводит отказ к одному виду, откуда бы он ни пришёл.
 *
 * Ошибки этого API — обычные HTTP-статусы (400, 401, 410, 429, 502), то есть
 * axios их бросает, а не возвращает. Поэтому разбирать `error.response` нужно
 * в каждом сервисе, и лучше одним местом.
 */
export const toOtpAuthError = (error: unknown): OtpAuthError => {
  const fallback = t('somethingWentWrong');

  if (!axios.isAxiosError<ErrorBody>(error)) {
    return { code: 'unknown', message: fallback };
  }

  const status = error.response?.status;
  const body = error.response?.data?.error;

  // `Retry-After` дублирует retry_after — берём то, что пришло.
  const header = Number(error.response?.headers?.['retry-after']);
  const retryAfter = body?.retry_after ?? (Number.isFinite(header) ? header : undefined);

  return {
    code: (body?.code as OtpAuthErrorCode) ?? 'unknown',
    message: body?.message || error.response?.data?.message || fallback,
    title: body?.title,
    retryAfter,
    status,
  };
};
